"use client";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { ResponsiveStyleValue } from "@mui/system";
import type { Song } from "~/types";
import type { ActionsVariant } from "./songActionsFactory";
import SongArtwork from "./SongArtwork";
import { useAudioControls, useAudioCurrentState } from "./useAudio";

import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";

type SongTileProps = SongTileCosmeticProps & {
  song: Song;
  playlist: Song[];
  actionsVariant?: ActionsVariant;
};

export type SongTileCosmeticProps = {
  /** Artwork size */
  size?: ResponsiveStyleValue<number | string>;
};

export default function SongTile({
  song,
  playlist,
  actionsVariant,
  size = 200,
}: SongTileProps) {
  const { playSong, pause, resume, open } = useAudioControls();
  const { isCurrent, isCurrentPlaying } = useAudioCurrentState(song.id);

  const handleClick = () => {
    if (isCurrentPlaying) {
      pause();
    } else if (isCurrent) {
      resume();
    } else {
      playSong(song, playlist, actionsVariant);
      open();
    }
  };

  return (
    <Paper
      onClick={handleClick}
      elevation={isCurrent ? 6 : 2}
      sx={{
        position: "relative",
        cursor: "pointer",
        borderRadius: 3,
        overflow: "hidden",
        width: size,
        flexShrink: 0,
        transition: "all 0.2s ease",
        outline: isCurrent ? "3px solid" : "3px solid transparent",
        outlineColor: isCurrent ? "primary.main" : "transparent",
        // show play button on hover (bottom-right)
        "&:hover .artworkOverlay": {
          opacity: 1,
          pointerEvents: "auto",
          "& .playButton:hover": {
            transform: "scale(1.2)",
            bgcolor: "rgba(0,0,0,0.6)",
          },
        },
        "&:hover": {
          transform: "scale(1.03)",
          elevation: 4,
          ".playingBars": {
            opacity: 0,
          },
        },
        ...(isCurrentPlaying && {
          transform: "scale(1.02)",
          elevation: 6,
        }),
        "@keyframes borderPulse": {
          "0%, 100%": { outlineColor: "primary.main" },
          "50%": { outlineColor: "primary.light" },
        },
        ...(isCurrentPlaying && {
          animation: "borderPulse 2s ease-in-out infinite",
        }),
      }}
    >
      <Box sx={{ position: "relative" }}>
        <SongArtwork song={song} size={size} />

        {/* Hover play/pause button */}
        <Box
          className="artworkOverlay"
          sx={{
            position: "absolute",
            inset: 0,
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "rgba(0,0,0,0.35)",
            opacity: 0,
            transition: "opacity 200ms",
            pointerEvents: "none",
          }}
        >
          <IconButton
            className="playButton"
            onClick={(e) => {
              e.stopPropagation();
              handleClick();
            }}
            size="small"
            sx={{
              position: "absolute",
              bottom: 8,
              right: 8,
              bgcolor: "rgba(0,0,0,0.35)",
              color: "common.white",
              boxShadow: 3,
              transform: "scale(0.95)",
              transition: "all 0.18s ease",
            }}
          >
            {isCurrentPlaying ? <PauseIcon /> : <PlayArrowIcon />}
          </IconButton>
        </Box>
      </Box>
      {/* Info */}
      <Box sx={{ p: 1.5 }}>
        <Typography variant="subtitle2" noWrap sx={{
          fontWeight: 700
        }}>
          {song.title}
        </Typography>
        {song.description && (
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              display: "-webkit-box",
              WebkitLineClamp: 1,
              WebkitBoxOrient: "vertical",
              overflow: "hidden"
            }}>
            {song.description}
          </Typography>
        )}
      </Box>
    </Paper>
  );
}
